import React from "react";
import "./AgentAssistant.css";
import { findRouteAction } from "../utils/agentRoutes";
import { getPayrollFlow, PAYROLL_PROCESS_FLOWS } from "../utils/payrollProcessAgent";

const SUGGESTIONS = [
  "Open employee list",
  "Show recruitment candidates",
  "Start payroll process",
  "Help me with attendance",
  "Where can I apply leave?",
  "Open payroll",
];

const AGENT_PROFILES = [
  {
    key: "chatbot",
    title: "HR Chatbot Agent",
    subtitle: "HR workflow helper",
    intro:
      "Answers HR workflow questions and opens employee, attendance, leave, payroll, and issue screens.",
  },
  {
    key: "recruitment",
    title: "Recruitment Agent",
    subtitle: "Hiring and sourcing helper",
    intro:
      "Helps with job posts, applicants, job descriptions, job titles, candidate screening, and shortlisting routes.",
  },
  {
    key: "payroll",
    title: "Payroll Process Agent",
    subtitle: "Automatic payroll guide",
    intro:
      "Guides mid-month and full-month payroll with notifications for attendance close, payroll entry, payslip status, approval, payment, close, and reports.",
  },
];

const HELP_TOPICS = [
  {
    keywords: ["employee", "staff", "worker", "profile", "add employee"],
    response:
      "For employee management, open Employees. From there you can view staff records, add a new employee, update profile details, documents, assets, promotions, and access codes.",
    routeQuery: "employee listing",
  },
  {
    keywords: [
      "recruitment",
      "candidate",
      "candidates",
      "applicant",
      "applicants",
      "resume",
      "cv",
      "sourcing",
      "job post",
      "hiring",
      "shortlist",
    ],
    response:
      "The Recruitment & Sourcing Agent can help you open job posts, applicant lists, job descriptions, job titles, and hiring setup screens. Use it to move faster through candidate sourcing and screening work.",
    routeQuery: "application list job post",
  },
  {
    keywords: ["attendance", "check in", "checkin", "check out", "checkout", "time"],
    response:
      "For attendance, use the Check In Out or Attendance screens. You can review employee logs, check-in records, activity logs, overtime requests, and attendance reports depending on your role.",
    routeQuery: "attendance check in out",
  },
  {
    keywords: ["leave", "holiday", "absence", "vacation"],
    response:
      "For leave work, open Leave. You can review leave requests, add leave, and check leave history. Employees usually use the leave form, while HR/sub-admin users can approve or manage records.",
    routeQuery: "leave listing",
  },
  {
    keywords: [
      "payroll",
      "payslip",
      "salary",
      "pay",
      "wage",
      "automatic payroll",
      "payroll process",
      "mid month salary",
      "full month salary",
      "paid instruction",
    ],
    response:
      "The Payroll Process Agent can guide the complete process: attendance close, payroll line entry, employee salary entries, payslip status, approval, payment, final payroll close, payslip view, and payroll report.",
    routeQuery: "payroll process",
  },
  {
    keywords: ["issue", "ticket", "incident", "complaint", "problem"],
    response:
      "For issues or complaints, use Employee Issues, Incident, or Complaints depending on the record type. The agent can open the issue list for you.",
    routeQuery: "employee issue",
  },
  {
    keywords: ["telegram", "whatsapp", "chatbot", "bot"],
    response:
      "This project already has Telegram/WhatsApp chatbot-related backend work. The next backend step is to store per-employee chatbot enable flags and expose them through the employee save/fetch APIs.",
    routeQuery: "employee listing",
  },
];

function getContext() {
  return {
    loginType: localStorage.getItem("login_type") || "user",
    employeeCode: localStorage.getItem("employee_code") || localStorage.getItem("uname") || "",
    organization: localStorage.getItem("org_name") || localStorage.getItem("organization_name") || "",
  };
}

function findTopic(text) {
  const query = text.toLowerCase();
  return HELP_TOPICS.find((topic) =>
    topic.keywords.some((keyword) => query.indexOf(keyword) !== -1)
  );
}

function makeFallbackResponse(text) {
  const action = findRouteAction(text);

  if (action) {
    return {
      text: `I found a likely match: ${action.label}. I can open that screen for you.`,
      action,
    };
  }

  return {
    text:
      "I can search the dashboard routes and open matching screens. Ask in simple words, for example: \"open employee list\", \"attendance report\", or \"payroll process\".",
  };
}

class AgentAssistant extends React.Component {
  constructor(props) {
    super(props);
    const context = getContext();
    const activeAgent = AGENT_PROFILES[0];

    this.state = {
      activeAgentKey: activeAgent.key,
      payrollFlowKey: "mid_month",
      isOpen: false,
      input: "",
      messages: [
        {
          from: "agent",
          text: `Hi, I am your ${activeAgent.title}. I can guide ${context.loginType.replace("_", " ")} users through HR tasks, recruitment work, and dashboard screens.`,
        },
      ],
    };

    // Scroll to bottom when messages update
    this.messagesEndRef = React.createRef();
  }

  componentDidUpdate(prevProps, prevState) {
    // Auto-scroll to latest message
    if (this.messagesEndRef.current) {
      setTimeout(() => {
        this.messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 0);
    }
  }

  toggleOpen = () => {
    this.setState((state) => ({ isOpen: !state.isOpen }));
  };

  setInput = (event) => {
    this.setState({ input: event.target.value });
  };

  selectAgent = (agent) => {
    this.setState((state) => ({
      activeAgentKey: agent.key,
      messages: [
        ...state.messages,
        {
          from: "agent",
          text: `${agent.title} selected. ${agent.intro}`,
        },
      ],
    }));
  };

  setPayrollFlow = (payrollFlowKey) => {
    const flow = getPayrollFlow(payrollFlowKey);
    this.setState((state) => ({
      payrollFlowKey,
      messages: [
        ...state.messages,
        {
          from: "agent",
          text: `${flow.label} selected. ${flow.description}`,
        },
      ],
    }));
  };

  notifyPayrollStep = (step, index) => {
    const action = findRouteAction(step.routeQuery);
    const title = `Step ${index + 1}: ${step.title}`;
    const text = `${title}. ${step.notification}`;

    // Request notification permission
    if (window.Notification && Notification.permission === "default") {
      Notification.requestPermission();
    }

    // Show browser notification if permitted
    if (window.Notification && Notification.permission === "granted") {
      try {
        new Notification(title, { body: step.notification });
      } catch (e) {
        console.warn("Notification error:", e);
      }
    }

    // Add message with action button
    this.setState((state) => ({
      messages: [
        ...state.messages,
        {
          from: "agent",
          text,
          action,
        },
      ],
    }));
  };

  notifyPayrollFlow = () => {
    const flow = getPayrollFlow(this.state.payrollFlowKey);
    const messages = flow.steps.map((step, index) => ({
      from: "agent",
      text: `Step ${index + 1}: ${step.title}. ${step.notification}`,
      action: findRouteAction(step.routeQuery),
    }));

    this.setState((state) => ({
      messages: [...state.messages, ...messages],
    }));
  };

  submitMessage = (text) => {
    const message = (text || this.state.input).trim();
    if (!message) return;

    const topic = findTopic(message);
    const topicAction = topic ? findRouteAction(topic.routeQuery || message) : null;
    const response = topic
      ? { text: topic.response, action: topicAction }
      : makeFallbackResponse(message);

    this.setState((state) => ({
      input: "",
      messages: [
        ...state.messages,
        { from: "user", text: message },
        { from: "agent", text: response.text, action: response.action },
      ],
    }));
  };

  openAction = (action) => {
    if (!action || !action.path) {
      console.warn("Invalid action or missing path:", action);
      return;
    }

    try {
      window.location.href = action.path;
    } catch (e) {
      console.error("Navigation error:", e);
    }
  };

  renderMessage(message, index) {
    if (!message || !message.text) return null;
    
    return (
      <div key={index} className={`agent-message agent-message-${message.from}`}>
        <div>{message.text}</div>
        {message.action && message.action.path && (
          <button
            type="button"
            className="agent-inline-action"
            onClick={() => this.openAction(message.action)}
          >
            Open {message.action.label || "Screen"}
          </button>
        )}
      </div>
    );
  }

  renderPayrollPanel() {
    const { payrollFlowKey } = this.state;
    const flow = getPayrollFlow(payrollFlowKey);

    if (!flow || !flow.steps) {
      return <div className="agent-payroll-panel">Loading payroll steps...</div>;
    }

    return (
      <div className="agent-payroll-panel">
        <div className="agent-payroll-flow">
          {Object.keys(PAYROLL_PROCESS_FLOWS).map((key) => (
            <button
              type="button"
              key={key}
              className={key === payrollFlowKey ? "active" : ""}
              onClick={() => this.setPayrollFlow(key)}
            >
              {PAYROLL_PROCESS_FLOWS[key].label}
            </button>
          ))}
        </div>

        <button
          type="button"
          className="agent-payroll-all"
          onClick={this.notifyPayrollFlow}
        >
          Show all payroll notifications
        </button>

        <div className="agent-payroll-steps">
          {flow.steps.map((step, index) => (
            <button
              type="button"
              key={`${payrollFlowKey}-${step.title}-${index}`}
              onClick={() => this.notifyPayrollStep(step, index)}
              title={step.title}
            >
              <span className="agent-payroll-step-number">{index + 1}</span>
              <span className="agent-payroll-step-title">{step.title}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  render() {
    const { activeAgentKey, isOpen, input, messages } = this.state;
    const activeAgent =
      AGENT_PROFILES.find((agent) => agent.key === activeAgentKey) || AGENT_PROFILES[0];

    return (
      <div className="agent-assistant">
        {isOpen && (
          <div className="agent-panel">
            <div className="agent-header">
              <div>
                <div className="agent-title">{activeAgent.title}</div>
                <div className="agent-subtitle">{activeAgent.subtitle}</div>
              </div>
              <button type="button" className="agent-close" onClick={this.toggleOpen}>
                ✕
              </button>
            </div>

            <div className="agent-switcher">
              {AGENT_PROFILES.map((agent) => (
                <button
                  type="button"
                  key={agent.key}
                  className={agent.key === activeAgentKey ? "active" : ""}
                  onClick={() => this.selectAgent(agent)}
                >
                  <span>{agent.title}</span>
                  <small>{agent.subtitle}</small>
                </button>
              ))}
            </div>

            <div className="agent-messages">
              {messages.map((message, index) => this.renderMessage(message, index))}
              <div ref={this.messagesEndRef} />
            </div>

            {activeAgentKey === "payroll" && this.renderPayrollPanel()}

            <div className="agent-suggestions">
              {SUGGESTIONS.map((suggestion) => (
                <button
                  type="button"
                  key={suggestion}
                  onClick={() => this.submitMessage(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>

            <div className="agent-input-row">
              <input
                value={input}
                onChange={this.setInput}
                onKeyDown={(event) => {
                  if (event.key === "Enter") this.submitMessage();
                }}
                placeholder="Ask about HR tasks..."
              />
              <button 
                type="button" 
                onClick={() => this.submitMessage()}
                disabled={!this.state.input.trim()}
              >
                Send
              </button>
            </div>
          </div>
        )}

        <button type="button" className="agent-launcher" onClick={this.toggleOpen}>
          AI
        </button>
      </div>
    );
  }
}

export default AgentAssistant;